import { useState, useEffect, useRef } from "react";
import client from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Boxes, Search, Loader2, PackageSearch, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STATUS = {
  in: { label: "In stock", cls: "text-emerald-600", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  low: { label: "Low stock", cls: "text-amber-600", chip: "bg-amber-50 text-amber-700 border-amber-200", Icon: AlertTriangle },
  out: { label: "Out of stock", cls: "text-red-600", chip: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
};

function Metric({ label, value, testid, accent }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 text-center" data-testid={testid}>
      <div className={cn("font-display text-2xl font-extrabold leading-none", accent || "text-ink")}>{value}</div>
      <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

export default function Stock() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [stock, setStock] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  const debounce = useRef(null);
  const skipSearch = useRef(false);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (skipSearch.current) { skipSearch.current = false; setResults([]); return; }
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await client.get("/inflow/products", { params: { q: q.trim() } });
        setResults(data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Search failed");
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(debounce.current);
  }, [q]);

  const pick = async (p) => {
    setSelected(p);
    setResults([]);
    skipSearch.current = true;
    setQ(p.name || "");
    setStock(null);
    setLoadingStock(true);
    try {
      const { data } = await client.get(`/inflow/products/${p.product_id}/stock`);
      setStock({ ...data, name: data.name || p.name, sku: data.sku || p.sku });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load stock");
      setSelected(null);
    } finally { setLoadingStock(false); }
  };

  const st = stock ? STATUS[stock.status] || STATUS.in : null;

  return (
    <div className="animate-fade-up mx-auto max-w-2xl">
      <PageHeader title="Stock check" subtitle="Search a synced inFlow product to see live available quantity" testid="stock-header" />

      <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]">
        <Label className="mb-1.5 block">Product name or SKU</Label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setSelected(null); setStock(null); }}
            placeholder="e.g. Alpine Grey 600x600"
            data-testid="stock-search-input"
            className="pl-9"
            autoFocus
          />
          {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
        </div>

        {results.length > 0 && (
          <div className="mt-2 max-h-72 overflow-y-auto rounded-xl border border-slate-100" data-testid="stock-results">
            {results.map((p) => (
              <button
                key={p.product_id}
                onClick={() => pick(p)}
                data-testid={`stock-result-${p.product_id}`}
                className="flex w-full items-center justify-between gap-3 border-b border-slate-50 px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-muted"
              >
                <span className="min-w-0 truncate text-sm font-medium text-ink">{p.name}</span>
                <span className="shrink-0 font-mono text-xs text-muted-foreground">{p.sku}</span>
              </button>
            ))}
          </div>
        )}
        {!searching && q.trim() && results.length === 0 && !selected && (
          <p className="mt-2 text-sm text-muted-foreground">No products match “{q}”. Make sure the catalogue is synced in Settings.</p>
        )}
      </div>

      {loadingStock && (
        <div className="flex items-center justify-center rounded-2xl border border-slate-100 bg-white p-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      )}

      {stock && !loadingStock && (
        <div className="animate-fade-up rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]" data-testid="stock-card">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-display text-lg font-bold text-ink" data-testid="stock-product-name">{stock.name}</div>
              <div className="mt-0.5 font-mono text-xs text-muted-foreground">{stock.sku || "—"}</div>
            </div>
            <span className={cn("flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold", st.chip)} data-testid="stock-status">
              <st.Icon className="h-3.5 w-3.5" />{st.label}
            </span>
          </div>

          <div className="mt-4 rounded-2xl border border-slate-100 bg-primary/5 p-5 text-center">
            <div className={cn("font-display text-5xl font-extrabold leading-none", st.cls)} data-testid="stock-available">{stock.available}</div>
            <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Available to sell</div>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-3">
            <Metric label="On hand" value={stock.on_hand} testid="stock-onhand" />
            <Metric label="Reserved" value={stock.reserved} testid="stock-reserved" />
            <Metric label="On order" value={stock.on_order} testid="stock-onorder" />
          </div>
        </div>
      )}

      {!stock && !loadingStock && !q.trim() && (
        <EmptyState icon={PackageSearch} title="Check stock levels" hint="Start typing a product name or SKU above to see its live available quantity from inFlow." />
      )}
    </div>
  );
}
